import '../../../../webmodule-common/other/ui/maps/google-map';
import '../../../../webmodule-common/other/ui/maps/google-place-autocomplete';
import { AddressEditor } from '../../../../webmodule-common/other/ui/address-editor';
import { AskConfirmation, buttonsYesNo } from '../../../../webmodule-common/other/ui/modal-confirmation';
import { cache } from '../../cache-impl/cache-registry';
import { Client } from '../../../api/dealer-api-interface-client';
import { ClientApi } from '../../../api/client-api';
import { clientContainerManagerFactory } from '../../clients/client-ui-adaptor';
import { ClientPicker } from '../../../clients/views/client-picker';
import { clone, compare } from '../../../../webmodule-common/other/clone';
import { constructAsync } from '../../../../webmodule-common/other/async-constructor';
import { ContactPicker } from '../../../clients/views/contact-picker';
import { createNewClient } from '../../clients/data/franchisee-client-container';
import { customElement, state } from 'lit/decorators.js';
import { determineProjectShippingAddress } from '../data/project-helper-functions';
import { DevelopmentError, showDevelopmentError } from '../../../../webmodule-common/other/development-error';
import { emptyAddress } from '../../../../webmodule-common/other/ui/maps/map-helpers';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid';
import { EventTemplate } from '../../../../webmodule-common/other/ui/events';
import { FieldType } from '../../../../webmodule-common/other/ui/databinding/data-tracker';
import {
  fireQuickInformationToast,
  fireQuickSuccessToast,
  fireQuickWarningToast
} from '../../../../webmodule-common/other/toast-away';
import { FranchiseeClient } from '../../../api/dealer-api-interface-franchisee';
import { FranchiseeClientView } from '../../clients/views/franchisee-client-view';
import { getApiFactory } from '../../../api/api-injector';
import { getBranchUserCache } from '../../cache-impl/dealer-branch-user-cache';
import { getQuoteContainerManager, QuoteLock } from '../../quotes/quote-ui-adapter';
import { html } from 'lit';
import { isAutoSaving, saveWithIndicator } from '../../../../webmodule-common/other/save-workflow';
import { isEmptyOrSpace, validId } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { LangFranchiseeProjectDetailView } from '../../../../localization/scripts/dealer-franchisee/projects/views/franchisee-project-detail-view-lang';
import { lockUIandExecute } from '../../../../webmodule-common/other/ui-lock';
import { PaymentProfilePicker } from '../../../franchisee/view/payment-profile-picker';
import { type PickerResult } from '../../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { ProjectDetailView, ProjectDetailViewOptions } from '../../../projects/views/project-detail-view';
import { ProjectState, ResourceType } from '../../../api/dealer-api-interface-project';
import { QuoteState } from '../../../api/dealer-api-interface-quote';
import { showError } from '../../../../webmodule-common/other/ui/show-error';
import { showValidations } from '../../../../webmodule-common/other/ui/modal-validationhandler';
import { TenantLoginPublicInfo } from '../../../api/dealer-api-interface-user';
import { updatePicker } from '../../../../webmodule-common/other/ui/templateresult/form-picker';
import { userDataStore } from '../../common/current-user-data-store';
import { WebmoduleSelect } from '../../../../webmodule-common/components/src/webmodule-components';
import { TermsConditionsPicker } from '../../../franchisee/view/terms-conditions-picker';

@customElement('wm-franchiseeprojectdetailview')
export class FranchiseeProjectDetailView extends ProjectDetailView {
  clientApi: ClientApi = getApiFactory().client();
  paymentProfileApi = getApiFactory().paymentProfile();
  termsCondtionsApi = getApiFactory().termsAndConditions();

  branchUserCache = getBranchUserCache();

  clientCache = cache().client;
  contactCache = cache().contact;
  primaryContactCache = cache().primaryContact;
  paymentProfileCache = cache().paymentProfile;
  termsConditionsCache = cache().termsAndConditions;

  clientPickerDisplay = '';
  contactPickerDisplay = '';
  clientTypePickerDisplay = '';
  termsConditionsDisplay = '';

  currentAuthorId = emptyGuid;

  @state()
  private _canCreateClient: boolean = false;

  constructor(options: ProjectDetailViewOptions) {
    super(options);

    const addField = (
      fieldName: string,
      propertyType?: FieldType,
      nullable?: boolean,
      editorFieldName?: string,
      data?: () => any
    ) => {
      this.dataTracker.addObjectBinding(
        data ?? (() => this.project),
        fieldName,
        editorFieldName ?? fieldName,
        propertyType ?? FieldType.string,
        nullable ?? false
      );
    };

    addField('clientId');
    addField('contactId');
    addField('clientTypeId');
    addField('termsAndConditionsId', undefined, true);
    addField('assignedToUserId');
  }

  async afterConstruction(): Promise<void> {
    await super.afterConstruction();
    await this.branchUserCache.prefetch();
    await this.projectManager.needsProject();
    await this.branchUserCache.addUsersToBranch(userDataStore.defaultBranch.id, [
      this.project.assignedToUserId,
      this.project.creationUserId,
      this.project.lastModifiedUserId
    ]);
  }

  async loadOrRefresh(): Promise<void> {
    await super.loadOrRefresh();

    await this.loadPickerDisplaysFromCache();

    this.currentAuthorId = this.project.assignedToUserId;
    this._canCreateClient = await this.projectManager.canChangeClient();
    this.requestUpdate();
  }

  getValidationErrors(): string[] {
    const errors: string[] = [];

    if (!this.project) {
      throw new DevelopmentError('Project is null');
    }
    if (this.project.state === ProjectState.Active) {
      const client = this.project.clientId;
      const contact = this.project.contactId;
      const clientType = this.project.clientTypeId;

      if (isEmptyOrSpace(client)) errors.push(LangFranchiseeProjectDetailView.selectClient());

      if (isEmptyOrSpace(contact)) errors.push(LangFranchiseeProjectDetailView.selectContact());

      if (isEmptyOrSpace(clientType)) errors.push(LangFranchiseeProjectDetailView.selectPaymentProfile());
    }

    return errors;
  }

  async createNewClient() {
    const container = await createNewClient(userDataStore.clientOwnerId);
    if (!container) {
      await showDevelopmentError('Client could not be created');
      return;
    }
    const qm = clientContainerManagerFactory(container);

    const qv = await constructAsync(
      new FranchiseeClientView({
        viewTitle: () => LangFranchiseeProjectDetailView.addClient(),
        clientContainerManager: qm
      })
    );
    await qv.showModal();
    const client = qv.view?.clientContainer.client;
    if (client && validId(client.id)) {
      this.selectClient(client);
    }
  }

  async addressModified(addressEditor: AddressEditor) {
    let editorShipping = addressEditor.isDefaultShipping;
    const editorAddress = addressEditor.address ? clone(addressEditor.address) : emptyAddress();

    const askConfirmation =
      !compare(editorAddress, this.project.defaultAddress) &&
      (!compare(editorShipping, this.project.shipToDefaultAddress) || !editorShipping);

    if (
      askConfirmation &&
      (await AskConfirmation(
        LangFranchiseeProjectDetailView.setDefaultShippingAddressQuestion(),
        buttonsYesNo(),
        undefined,
        LangFranchiseeProjectDetailView.setDefaultShippingAddressTitle()
      ))
    ) {
      editorShipping = true;
    }

    this.project.defaultAddress = editorAddress;

    this.project.shipToDefaultAddress = editorShipping;

    this.project.shippingNotes = addressEditor.shippingNotes ? addressEditor.shippingNotes : '';

    this.requestUpdate();
  }

  protected template(): EventTemplate {
    const forms = this.forms;
    const allowChangeClient = !this.projectManager.isReadonly() && this._canCreateClient;

    const createClientEvent = async () => lockUIandExecute(async () => await this.createNewClient());
    const createClientTemplate = allowChangeClient
      ? html`${forms.buttonPrimary(LangFranchiseeProjectDetailView.newButton(), {
          events: { click: createClientEvent },
          slot: 'suffix',
          class: 'project-client-picker-button'
        })} `
      : html` <webmodule-icon slot="suffix" name="fas-ellipsis" library="fa"></webmodule-icon>`;

    const eventPickClient = async (): Promise<PickerResult | undefined> => {
      await this.selectClient();
      return undefined;
    };
    const clientPicker = allowChangeClient
      ? forms.pickerRequired(
          'clientId',
          this.clientPickerDisplay,
          eventPickClient,
          LangFranchiseeProjectDetailView.client(),
          createClientTemplate
        )
      : forms.pickerReadonly('clientId', this.clientPickerDisplay, LangFranchiseeProjectDetailView.client());
    const eventPickContact = async (): Promise<PickerResult | undefined> => {
      await this.selectContact();
      return undefined;
    };

    const contactPicker = forms.picker(
      'contactId',
      this.contactPickerDisplay,
      eventPickContact,
      LangFranchiseeProjectDetailView.contact()
    );

    const eventPickClientType = async (): Promise<PickerResult | undefined> => {
      await this.selectClientType();
      return undefined;
    };

    const clientTypePicker = forms.picker(
      'clientTypeId',
      this.clientTypePickerDisplay,
      eventPickClientType,
      LangFranchiseeProjectDetailView.paymentProfile()
    );

    const eventPickTerms = async (): Promise<PickerResult | undefined> => {
      await this.selectTerms();
      return undefined;
    };

    const termsPicker = forms.picker(
      'termsAndConditionsId',
      this.termsConditionsDisplay,
      eventPickTerms,
      LangFranchiseeProjectDetailView.termsConditions()
    );

    const addressTemplate1 = forms.addressEditor(
      LangFranchiseeProjectDetailView.projectSiteInstallationAddress(),
      'physicalAddressEditor',
      {
        onAddressChanged: (e: Event) => this.addressModified(e.currentTarget as AddressEditor),
        address: this.project.defaultAddress,
        shippingNotes: this.project.shippingNotes ?? undefined,
        readonly: this.projectManager.isReadonly(),
        isDefaultShippingVisible: true,
        isDefaultShipping: this.project.shipToDefaultAddress
      }
    );

    const users = this.branchUserCache.getBranchUsers(userDataStore.defaultBranch.id);
    const userToOption = (u: TenantLoginPublicInfo) => ({ text: u.friendlyName ?? '', value: u.id, disabled: false });

    return html`
      <form class="form-two-col frm-project-details">
        <div class="row">
          <div>
            ${forms.text('title', LangFranchiseeProjectDetailView.title(), 100)} ${clientPicker} ${contactPicker}
            ${clientTypePicker}
            ${termsPicker}
          </div>
          <div>
            ${forms.arraySelect('assignedToUserId', users, userToOption, {
              title: LangFranchiseeProjectDetailView.author(),
              events: { change: () => this.onAssignedToUserSelected() }
            })}
            ${forms.money('budget', LangFranchiseeProjectDetailView.projectBudget())}
            ${forms.note('description', LangFranchiseeProjectDetailView.description(), 2950, undefined, undefined, 5)}
          </div>
        </div>
        ${addressTemplate1}
      </form>
    `;
  }

  protected async onAssignedToUserSelected() {
    //This is a single value select so we can safely assume the type as string
    const assignedToUserId = this.dataTracker.getEditorValue('assignedToUserId') as string;
    if (!assignedToUserId) return;

    const projectAuthor = this.project.assignedToUserId;
    if (assignedToUserId === projectAuthor) {
      return;
    }
    const quoteResources = this.projectManager.container.resources?.filter(r => r.typeOf === ResourceType.Quote);
    if (!quoteResources || quoteResources.length == 0) {
      return;
    }

    this.dataBinding.setValue('assignedToUserId', assignedToUserId);

    const saveResult = await this.internalSave();
    if (!saveResult) {
      return;
    }
    const quoteIds = quoteResources.map(qr => qr.resourceId);
    if (quoteIds.length > 0) {
      const existingQuotesNeedAuthorChange = await AskConfirmation(
        LangFranchiseeProjectDetailView.changeQuoteAuthorsQuestion(),
        buttonsYesNo(),
        undefined,
        LangFranchiseeProjectDetailView.newProjectAuthorTitle()
      );
      if (existingQuotesNeedAuthorChange) {
        const quoteIdsToUpdate: string[] = [];
        const quoteLocks: QuoteLock[] = [];
        const lockedQuotes: { quoteTitle: string; username: string }[] = [];

        if (quoteIds.length > 0) {
          const notifyLockedQuotes = () => {
            const lockedQuoteText = lockedQuotes
              .map(x =>
                LangFranchiseeProjectDetailView.lockedQuoteText({ quoteTitle: x.quoteTitle, username: x.username })
              )
              .join('<br/>');
            fireQuickWarningToast(LangFranchiseeProjectDetailView.lockedQuotesWarning({ lockedQuoteText }), 7000);
          };

          try {
            for (const quoteId of quoteIds) {
              const qcm = getQuoteContainerManager(quoteId);
              await qcm.needsQuote();
              if (qcm.quoteState == QuoteState.Active) {
                const quoteLock = new QuoteLock(quoteId, async () => {});
                await quoteLock.lock();
                if (quoteLock.canUseResource && quoteLock.isLockOwner) {
                  quoteLocks.push(quoteLock);
                  quoteIdsToUpdate.push(quoteId);
                } else {
                  lockedQuotes.push({ quoteTitle: qcm.quoteTitle, username: quoteLock.lockOwnerName });
                }
              }
            }
            if (quoteIdsToUpdate.length > 0) {
              const result = await getApiFactory()
                .quote()
                .setAuthorForQuotes({ userId: assignedToUserId, quoteIds: quoteIdsToUpdate });
              if (result) {
                fireQuickSuccessToast(LangFranchiseeProjectDetailView.quotesAuthorsChanged(), 5000);
                if (lockedQuotes.length > 0) {
                  notifyLockedQuotes();
                }
              } else {
                fireQuickWarningToast(LangFranchiseeProjectDetailView.unableToChangeQuoteAuthors());
              }
            } else {
              if (lockedQuotes.length > 0) {
                notifyLockedQuotes();
              } else {
                fireQuickInformationToast(LangFranchiseeProjectDetailView.noQuotesRequireUpdating());
              }
            }
          } catch (e) {
            showError(e as Error);
          } finally {
            for (const quoteLock of quoteLocks) {
              await quoteLock.release();
            }
          }
        }
      }
    }

    this.requestUpdate();
  }

  protected async internalSave(): Promise<boolean> {
    await this.prepareForSave();
    const validations = this.getValidationErrors();
    if (validations.length > 0) {
      await showValidations(validations);
      return false;
    }
    const result = await saveWithIndicator(async () => await this.projectManager.saveProject(isAutoSaving()));
    return result;
  }

  protected changeDefaultShipping(value: boolean) {
    this.projectManager.project.shipToDefaultAddress = value;
    this.requestUpdate(); //no wait
  }

  protected async requiredAddressFieldFocusOut(fieldName: string) {
    const fieldValue = this.dataBinding.getValue(fieldName);
    if (isEmptyOrSpace(fieldValue)) {
      this.projectManager.project.shipToDefaultAddress = false;
    }
    this.requestUpdate();
  }

  private async loadPickerDisplaysFromCache() {
    this.clientPickerDisplay = (await this.clientCache.get(this.project.clientId))?.displayValue ?? '';
    this.contactPickerDisplay = (await this.contactCache.get(this.project.contactId))?.displayValue ?? '';
    this.clientTypePickerDisplay = (await this.paymentProfileCache.get(this.project.clientTypeId))?.displayValue ?? '';
    this.termsConditionsDisplay = (await this.termsConditionsCache.get(this.project.termsAndConditionsId))?.displayValue ?? '';
  }

  private async selectClient(client?: Client) {
    const selectedClient = client?.id ?? (await ClientPicker())?.id;

    if (!selectedClient) {
      return;
    }

    const clientCacheItem = await this.clientCache.get(selectedClient);
    const clientData = clientCacheItem?.data;
    if (!clientData) return;

    //Getting here: we have a client selected and loaded from the cache
    this.clientPickerDisplay = clientCacheItem?.displayValue ?? '';
    this.forms.updatePicker('clientId', { id: selectedClient, value: this.clientPickerDisplay });
    const franchiseeClient = clientData.franchiseeClient as FranchiseeClient;
    if (!userDataStore.defaultBranch) await userDataStore.loadCoreDetails();

    //TODO: don't default project address to franchisee for now, we'll revisit this later in task 213557
    const addressToUse = determineProjectShippingAddress(
      undefined,
      false,
      clientData.client.physicalAddress,
      clientData.client.shipToPhysicalAddress
    );
    console.log('Getting address', addressToUse);

    this.project.defaultAddress = addressToUse;

    const primaryContact = await this.primaryContactCache.getData(selectedClient);
    const contact = await this.contactCache.get(primaryContact?.contactId ?? emptyGuid);
    this.contactPickerDisplay = contact?.displayValue ?? '';
    this.forms.updatePicker('contactId', {
      id: primaryContact?.contactId ?? emptyGuid,
      value: this.contactPickerDisplay
    });

    const defaultPaymentProfile = await this.paymentProfileCache.get(franchiseeClient.paymentProfileId);

    if (defaultPaymentProfile && defaultPaymentProfile.id !== this.dataBinding.getValue('clientTypeId')) {
      await this.updateClientTypeAndPickerDisplay(defaultPaymentProfile.id);
    }

    const defaultTerms = await this.termsConditionsCache.get(franchiseeClient.termsAndConditionsId);
    if (defaultTerms && defaultTerms.id !== this.dataBinding.getValue('termsAndConditionsId')) {
      await this.updateTermsPickerDisplay(defaultTerms.id);
    }

    this.requestUpdate();
  }

  private async updateClientTypeAndPickerDisplay(id: string) {
    const paymentProfile = await this.paymentProfileCache.get(id);

    if (paymentProfile && paymentProfile.id !== this.dataBinding.getValue('clientTypeId')) {
      this.clientTypePickerDisplay = paymentProfile.displayValue;
      this.forms.updatePicker('clientTypeId', { id: paymentProfile.id, value: paymentProfile.displayValue });
    }
  }

  private async updateTermsPickerDisplay(id: string) {
    const terms = await this.termsConditionsCache.get(id);

    if (terms && terms.id !== this.dataBinding.getValue('termsAndConditionsId')) {
      this.termsConditionsDisplay = terms.displayValue;
      this.forms.updatePicker('termsAndConditionsId', { id: terms.id, value: terms.displayValue });
    }
  }

  private async selectContact() {
    const selectedClient = this.dataBinding.getValue('clientId');
    const select = this.querySelector(`#${this.dataBinding.field('contactId')}`) as WebmoduleSelect | null;

    if (selectedClient == emptyGuid) {
      this.dataBinding.setValue('contactId', emptyGuid);
      this.contactPickerDisplay = '';
      if (select) {
        updatePicker(select, emptyGuid, this.contactPickerDisplay);
      }

      fireQuickWarningToast(LangFranchiseeProjectDetailView.selectClientFirst(), 1000);
    } else {
      const selectedContact = await ContactPicker(selectedClient);

      if (selectedContact) {
        this.dataBinding.setValue('contactId', selectedContact.id);
        this.contactPickerDisplay = selectedContact.name ?? '';
        if (select) {
          updatePicker(select, selectedContact.id, this.contactPickerDisplay);
        }
      }
    }

    this.requestUpdate();
  }

  private async selectClientType() {
    const selectedClientType = await PaymentProfilePicker(this.paymentProfileApi);

    if (selectedClientType) {
      await this.updateClientTypeAndPickerDisplay(selectedClientType.id);
      this.requestUpdate();
    }
  }

  private async selectTerms() {
    const selectedTerms = await TermsConditionsPicker(this.termsCondtionsApi);

    if (selectedTerms) {
      await this.updateTermsPickerDisplay(selectedTerms.id);
      this.requestUpdate();
    }
  }
}
